import { Methods } from '../../api/methods.js';
import { ZaparooClient } from '../../client/client.js';
import { resolvePaths, saveDeviceMetadata } from '../../client/config.js';
import { resolveDevice } from '../../client/resolver.js';
import { TraceWriter } from '../../client/trace.js';
import { CredentialStore } from '../../crypto/storage.js';
import type { GlobalOptions, ParsedArgs } from '../args.js';
import { flag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import { type BinaryResponse, writeBase64Output } from '../files.js';
import type { CommandResult } from '../output.js';
import { confirmationGranted } from '../policy.js';

export async function withClient<T>(
  options: GlobalOptions,
  fn: (client: ZaparooClient) => Promise<T>,
): Promise<T> {
  const device = await resolveDevice(options);
  const paths = resolvePaths(options.configPath, options.credentialsPath);
  const store = new CredentialStore(paths.credentialsPath);
  const credentials = store.getCredentials(device.id, device.aliases);
  const client = new ZaparooClient(device, {
    credentials,
    connectTimeoutMs: options.timeoutSeconds * 1000,
    requestTimeoutMs: options.timeoutSeconds * 1000,
    trace: options.trace ? new TraceWriter() : undefined,
  });
  try {
    const info = await client.connect();
    try {
      saveDeviceMetadata(device, info, paths.configPath);
    } catch {
      // Metadata caching must not fail requested device operation.
    }
    return await fn(client);
  } finally {
    await client.close();
  }
}

export function kvArgs(entries: string[]): Record<string, string> | undefined {
  if (entries.length === 0) return undefined;
  const result: Record<string, string> = {};
  for (const entry of entries) {
    const [key, ...rest] = entry.split('=');
    result[key] = rest.join('=');
  }
  return result;
}

export function pickDefined(data: Record<string, unknown>): Record<string, unknown> | undefined {
  const result = Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined),
  );
  return Object.keys(result).length > 0 ? result : undefined;
}

export async function rawRequest<T>(
  args: ParsedArgs,
  method: string,
  params?: Record<string, unknown>,
): Promise<T> {
  return withClient(args.options, (client) =>
    client.request<T>(method, params ? pickDefined(params) : undefined),
  );
}

export async function request(
  args: ParsedArgs,
  method: string,
  params?: Record<string, unknown>,
): Promise<CommandResult> {
  return { data: await rawRequest(args, method, params) };
}

export async function downloadLogs(
  args: ParsedArgs,
  missingOutputMessage: string,
): Promise<CommandResult> {
  const output = flag(args.flags, 'output');
  if (!output) throw new CliError(missingOutputMessage, ExitCode.Usage);
  const response = await withClient(args.options, (client) =>
    client.request<BinaryResponse>(Methods.SettingsLogsDownload),
  );
  return { data: writeBase64Output(response, output) };
}

export function requireConfirmation(args: ParsedArgs, description: string, flagName = 'yes'): void {
  if (confirmationGranted(args, flagName)) return;
  if (args.options.policy === 'read-only') {
    throw new CliError(`${description}; policy read-only blocks this command`, ExitCode.Usage, {
      kind: 'policy',
      policy: args.options.policy,
    });
  }
  throw new CliError(`${description}; rerun with --yes to confirm`, ExitCode.Usage, {
    kind: 'confirmation-required',
    policy: args.options.policy,
  });
}

export function validatePairRole(role: string | undefined): string | undefined {
  if (role && role !== 'member' && role !== 'admin') {
    throw new CliError('--role must be member or admin', ExitCode.Usage);
  }
  return role;
}

export function parseJson(raw: string, label: string): unknown {
  try {
    return JSON.parse(raw);
  } catch (error) {
    throw new CliError(
      `${label} must be valid JSON: ${error instanceof Error ? error.message : String(error)}`,
      ExitCode.Usage,
    );
  }
}

export function parseJsonObject(raw: string, label: string): Record<string, unknown> {
  const parsed = parseJson(raw, label);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new CliError(`${label} must be a JSON object`, ExitCode.Usage);
  }
  return parsed as Record<string, unknown>;
}
